import { useState, useEffect, Fragment } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getCurrencySymbol } from "../lib/currency";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { formatTimeAMPM } from "../lib/utils";
import { getOrgTimezone, getTemporalStatus as getTemporalStatusTZ } from "../lib/timezone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import { MembershipModal } from "../components/MembershipModal";
import { DatePicker } from "../components/DatePicker";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { MultiSelect } from "../components/ui/multi-select";
import { QuickAttendanceDialog } from "../components/QuickAttendanceDialog";
import { toast } from "sonner";
import { Plus, Search, Download, Users, UserCheck, UserX, UserPlus, Clock, Eye, CreditCard, Filter, ChevronDown, Edit2, X, Phone, Trash2, PackageX, Package, ArrowRight, CalendarDays, Wallet, ChevronLeft, ChevronRight, Stethoscope, MapPin, Heart, StickyNote, MessageSquare, History, Calendar, Dumbbell, Bell } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_FILTERS = { name: "", phone: "", gender: "", status: "", packageIds: [], joiningFrom: "", joiningTo: "", expiryFrom: "", expiryTo: "" };

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

import { WhatsApp } from "../components/ui/whatsapp";

export default function Members({ defaultStatus = "" }) {
  const { currentBranch, organization, hasPermission } = useAuth();
  const currencySymbol = getCurrencySymbol(organization?.currency);
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  const canCreate = hasPermission("create_member");
  const canEdit = hasPermission("edit_member");
  const canDelete = hasPermission("delete_member");
  const canExport = hasPermission("export_members");
  const canAssign = hasPermission("create_membership");
  const canViewMemberships = hasPermission("view_memberships");
  const canViewFollowups = hasPermission("view_followups");
  const canViewAttendance = hasPermission("view_attendance");

  const initialStatus = location.state?.status || defaultStatus || "";

  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, status: initialStatus });
  const [packages, setPackages] = useState([]);
  const [showMembershipModal, setShowMembershipModal] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, member: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pkgGuard, setPkgGuard] = useState({ open: false, reason: "none" });
  const [photoDialog, setPhotoDialog] = useState({ open: false, src: "", name: "" });
  const [membershipDialog, setMembershipDialog] = useState({ open: false, member: null });
  const [quickAttendanceDialog, setQuickAttendanceDialog] = useState({ open: false, member: null });

  useEffect(() => {
    if (location.state?.status !== undefined) {
      const newFilters = { ...EMPTY_FILTERS, status: location.state.status };
      setFilters(newFilters);
      setPage(1);
      fetchData(newFilters, 1, limit);
    }
  }, [location.state]);

  useEffect(() => { fetchData(filters, page, limit); }, [page, limit, currentBranch]);

  const fetchData = async (activeFilters = filters, currentPage = page, currentLimit = limit) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", currentLimit);
      if (activeFilters.name) params.append("name", activeFilters.name);
      if (activeFilters.phone) params.append("phone", activeFilters.phone);
      if (activeFilters.gender) params.append("gender", activeFilters.gender);
      if (activeFilters.status) params.append("status", activeFilters.status);
      if (activeFilters.joiningFrom) params.append("joiningFrom", activeFilters.joiningFrom);
      if (activeFilters.joiningTo) params.append("joiningTo", activeFilters.joiningTo);
      if (activeFilters.packageIds?.length > 0) params.append("packageIds", activeFilters.packageIds.join(","));
      if (activeFilters.expiryFrom) params.append("expiryFrom", activeFilters.expiryFrom);
      if (activeFilters.expiryTo) params.append("expiryTo", activeFilters.expiryTo);
      if (currentBranch?.id) params.append("branchId", currentBranch.id);

      const [mRes, sRes, pRes] = await Promise.all([
        api.get(`/members?${params.toString()}`),
        api.get(`/members/stats${currentBranch ? `?branchId=${currentBranch.id}` : ""}`),
        api.get("/packages"),
      ]);

      setMembers(mRes.data.members);
      setTotal(mRes.data.total);
      setStats(sRes.data);
      setPackages(pRes.data.packages || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getPackageName = (packageId) =>
    packageId ? packages.find(p => p.id === packageId)?.name || null : null;

  const handleAssign = (e, member) => {
    if (e) e.stopPropagation();
    const allPkgs = packages || [];
    if (allPkgs.length === 0) { setPkgGuard({ open: true, reason: "none" }); return; }
    const activePkgs = allPkgs.filter(p => p.status === "Active");
    if (activePkgs.length === 0) { setPkgGuard({ open: true, reason: "inactive" }); return; }
    setShowMembershipModal(member);
  };

  const openPhoto = (e, m) => {
    e.stopPropagation();
    if (!m.photo) return;
    setPhotoDialog({ open: true, src: `${BACKEND_URL}${m.photo}`, name: m.name });
  };

  const handleSearch = () => {
    setPage(1);
    fetchData(filters, 1, limit);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const cleared = { ...EMPTY_FILTERS };
    setFilters(cleared);
    setPage(1);
    fetchData(cleared, 1, limit);
    setShowFilters(false);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filters.name) params.append("name", filters.name);
      if (filters.gender) params.append("gender", filters.gender);
      if (filters.status) params.append("status", filters.status);
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const response = await fetch(`${BACKEND_URL}/api/members/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "members.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const handleDelete = async (id) => {
    setDeleteLoading(true);
    try {
      await api.delete(`/members/${id}`);
      toast.success("Member deleted successfully");
      setDeleteModal({ open: false, member: null });
      fetchData();
    } catch { toast.error("Failed to delete member"); }
    finally { setDeleteLoading(false); }
  };

  const toggleRow = (id) => setExpandedRowId(prev => (prev === id ? null : id));

  const statusColor = (s) => ({
    Active: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
    Expired: "bg-red-500/15 text-red-500 border-red-500/20",
    Partial: "bg-amber-500/15 text-amber-500 border-amber-500/20",
    New: "bg-blue-500/15 text-blue-500 border-blue-500/20",
    Pending: "bg-blue-500/15 text-blue-500 border-blue-500/20",
    Inactive: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  }[s] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/20");

  const followupStatusColor = (s) => ({
    Open: "bg-blue-500/15 text-blue-500 border-blue-500/20",
    Responded: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
    "Not Interested": "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
    "Not Responded": "bg-orange-500/15 text-orange-500 border-orange-500/20",
    Closed: "bg-zinc-700/15 text-zinc-500 border-zinc-700/20",
    "Need Followup": "bg-purple-500/15 text-purple-500 border-purple-500/20",
  }[s] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/20");

  const TEMPORAL_STATUS_COLORS = {
    Upcoming: "bg-cyan-500/15 text-cyan-500 border-cyan-500/20",
    Overdue: "bg-red-500/15 text-red-500 border-red-500/20",
    Closed: "bg-zinc-500/15 text-zinc-500 border-zinc-500/20",
  };

  const getTemporalStatus = (f) => {
    const tz = getOrgTimezone(organization);
    return getTemporalStatusTZ(f, tz);
  };

  const displayStatus = (s) => s === "Pending" ? "New" : (s || "New");

  const renderMemberDetails = (m) => {
    return (
      <div className="flex flex-col">
        <div className="grid grid-cols-2 gap-2 p-2 border-b border-border">
          <a
            href={`tel:${m.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md active:scale-95 px-2"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-950/50 transition-colors group-hover:bg-sky-100 dark:group-hover:bg-sky-900/50">
              <Phone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
            </div>
            <div className="flex flex-col items-start leading-tight min-w-0">
              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                Mobile Call
              </span>
              <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                Call Now
              </span>
            </div>
          </a>
          <a
            href={`https://wa.me/${m.phone}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md active:scale-95 px-2"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/50 transition-colors group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50">
              <WhatsApp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex flex-col items-start leading-tight min-w-0">
              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                Message
              </span>
              <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                WhatsApp
              </span>
            </div>
          </a>
        </div>
        {/* Action Bar */}
        <div className="flex items-center justify-between gap-2 p-2 border-b border-border bg-secondary/20">
          <div className="text-xs text-muted-foreground font-mono hidden md:block">
            Member ID: #{m.memberNumber}
          </div>
          <div className="flex items-center gap-1 md:gap-2 ml-auto">
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
              onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}`); }}
              data-testid={`view-member-${m.id}`}>
              <Eye className="h-3.5 w-3.5" /> <span className="hidden md:block">View Details</span>
            </Button>
            {canEdit && (
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
                onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}/edit`); }}
                data-testid={`edit-member-${m.id}`}>
                <Edit2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Edit Member</span>
              </Button>
            )}
            {m.membershipCount > 0 && (
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
                onClick={(e) => { e.stopPropagation(); setMembershipDialog({ open: true, member: m }); }}
                data-testid={`view-membership-${m.id}`}>
                <CreditCard className="h-3.5 w-3.5" /> <span className="hidden md:block">Memberships</span>
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
              onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}/?tab=workout`); }}
              data-testid={`view-workout-${m.id}`}>
              <Dumbbell className="h-3.5 w-3.5" /> <span className="hidden md:block">Workouts</span>
            </Button>
            {canViewAttendance && (
              <div className="inline-flex rounded-md border border-border shadow-sm bg-background items-center shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1 font-heading uppercase tracking-wide rounded-l-md rounded-r-none px-2.5 hover:bg-secondary text-primary"
                  onClick={(e) => { e.stopPropagation(); setQuickAttendanceDialog({ open: true, member: m }); }}
                  data-testid={`quick-attendance-${m.id}`}
                >
                  <UserCheck className="h-3.5 w-3.5" /> <span className="hidden md:block">Attendance</span>
                </Button>
                <div className="w-[1px] bg-border h-5 shrink-0" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1.5 font-heading uppercase tracking-wide rounded-r-md rounded-l-none px-2 hover:bg-secondary text-muted-foreground"
                  onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}/?tab=attendance`); }}
                  data-testid={`view-attendance-${m.id}`}
                  title="View Attendance History"
                >
                  <Clock className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            {canDelete && (
              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto"
                onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, member: m }); }}
                data-testid={`delete-member-${m.id}`}>
                <Trash2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Delete Member</span>
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
          {/* Left Column: Membership & Payment info */}
          <div className="lg:col-span-4 space-y-6 p-3">
            {m.membershipCount > 0 ? (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">All Memberships ({m.membershipCount}) <a onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}?tab=memberships`); }} className="text-[10px] text-blue-500 ml-2 cursor-pointer font-normal capitalize tracking-tighter text-blue-500 hover:underline">View all</a> </p>
                  <div className="space-y-2">
                    {(m.allMemberships || []).map((ms) => (
                      <div key={ms.id} className="flex flex-col items-start gap-3 bg-background/50 rounded-lg px-3 py-2 text-xs">
                        <div className="flex items-center gap-2">
                          <Package className="h-3.5 w-3.5 text-primary shrink-0" />
                          <span className="font-medium min-w-[120px]">{ms.packageName || getPackageName(ms.packageId) || "—"}</span>
                          <Badge className={`${statusColor(ms.status)} border text-[10px] font-mono uppercase`}>{ms.status}</Badge>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="font-mono text-muted-foreground">{ms.startDate?.slice(0, 10)} → {ms.endDate?.slice(0, 10)}</span>
                        </div>
                        {
                          canViewMemberships && (
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-emerald-500">Paid: {currencySymbol}{(ms.paidAmount || 0).toLocaleString()}</span>
                              {ms.remainingAmount > 0 && (
                                <span className="font-mono text-amber-500">Due: {currencySymbol}{ms.remainingAmount.toLocaleString()}</span>
                              )}
                              {ms.partialPaymentDate && (
                                <span className="font-mono text-amber-500">Due Date: {ms.partialPaymentDate.slice(0, 10)}</span>
                              )}
                            </div>
                          )
                        }
                      </div>
                    ))}
                  </div>
                </div>
                {
                  canViewMemberships && (
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-2 ">
                      <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground">Last Payment <a onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}?tab=payments`); }} className="text-[10px] text-blue-500 ml-2 cursor-pointer tracking-tighter font-normal capitalize text-blue-500 hover:underline">View all</a></p>
                      <div className="space-y-1 px-3 py-2">
                        {m.lastPayment ? (
                          <div className="flex items-center gap-3 text-sm">
                            <span className="font-mono">{m.lastPayment.date?.slice(0, 10)}</span>
                            <Badge variant="outline" className="text-[10px] font-mono">{m.lastPayment.paymentMode}</Badge>
                            <span className="font-mono font-medium text-emerald-500">{currencySymbol}{(m.lastPayment.amount || 0).toLocaleString()}</span>
                          </div>
                        ) : <p className="text-muted-foreground text-sm">No payments recorded</p>}
                      </div>
                      <div className="space-y-1 px-3 ">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total Summary</p>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="font-mono text-emerald-500">Paid: {currencySymbol}{(m.totalPaid || 0).toLocaleString()}</span>
                          {(m.totalRemaining || 0) > 0 && (
                            <span className="font-mono text-amber-500">Due: {currencySymbol}{(m.totalRemaining || 0).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                }
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-4 text-center border-2 border-dashed border-border rounded-xl">
                <PackageX className="h-8 w-8 text-muted-foreground mb-2 opacity-50" />
                <p className="text-muted-foreground text-sm italic">No active membership found.</p>
                {canAssign && (
                  <Button variant="link" size="sm" className="text-primary mt-1 h-auto p-0" onClick={(e) => handleAssign(e, m)}>Assign one now <ArrowRight className="h-3 w-3 ml-1" /></Button>
                )}
              </div>
            )}
          </div>

          {/* Middle Column: Personal & Emergency info */}
          <div className="lg:col-span-4 space-y-6 lg:border-l lg:border-border  border-t md:border-t-0">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 p-3">
              <div>
                <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground flex items-center gap-1.5 mb-1"><CalendarDays className="h-3 w-3" /> Joining Date</p>
                <p className="text-sm font-normal">{m.joiningDate ? m.joiningDate.slice(0, 10) : "—"}</p>
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground flex items-center gap-1.5 mb-1"><Stethoscope className="h-3 w-3" /> Medical Issue</p>
                <p className={`text-sm font-semibold ${m.medicalIssue ? "text-red-500" : "text-emerald-500"}`}>
                  {m.medicalIssue || "None Reported"}
                </p>
              </div>
              {
                m.address && (
                  <div className="col-span-2">
                    <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground flex items-center gap-1.5 mb-1"><MapPin className="h-3 w-3" /> Address</p>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {m.address || "—"}
                      {(m.city || m.state) && <span className="text-xs">,{[m.city, m.state, m.pincode].filter(Boolean).join(", ")}</span>}
                    </p>
                  </div>
                )
              }
              {
                m.emergencyContactName && (
                  <div className=" pt-2">
                    <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground flex items-center gap-1.5 mb-2"><Heart className="h-3 w-3" /> Emergency Contact</p>
                    <div className="bg-background/40 rounded-lg p-3 border border-border/50">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-sm font-bold">{m.emergencyContactName || "—"}</p>
                        <Badge variant="outline" className="text-[9px] h-4 font-mono uppercase">{m.emergencyContactRelation || "Relation"}</Badge>
                      </div>
                      {
                        (m.emergencyContactPhone && m.emergencyContactPhone.trim().length > 5) && (
                          <p className="text-xs font-mono text-primary flex items-center gap-1.5"><Phone className="h-3 w-3" /> {(m.emergencyContactPhone && m.emergencyContactPhone.trim().length > 5) ? m.emergencyContactPhone : "—"}</p>
                        )
                      }
                    </div>
                  </div>
                )
              }
              <div className="col-span-2 pt-2">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-1"><StickyNote className="h-3 w-3" /> Notes</p>
                <p className="text-xs italic text-muted-foreground leading-relaxed">{m.notes || "No additional notes provided."}</p>
              </div>
            </div>
          </div>

          {
            canViewFollowups && (
              <div className="lg:col-span-4 space-y-6 lg:border-l lg:border-border p-3 border-t md:border-t-0">
                <div>
                  <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-4 flex items-center md:justify-between justify-start gap-2">
                    <span>All Follow-ups</span>
                    <a onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}?tab=followups`); }} className="text-[10px] text-blue-500 cursor-pointer font-normal capitalize tracking-tighter hover:underline">View all</a>
                  </p>

                  {m.activeFollowups && m.activeFollowups.length > 0 ? (
                    <div className="space-y-3">
                      <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1 -mr-1 [scrollbar-width:thin] [scrollbar-color:rgba(0,0,0,0.1)_transparent]">
                        {m.activeFollowups.map((f) => (
                          <div key={f.id} className="bg-background/50 rounded-lg px-2 py-1.5 border border-border/50 relative overflow-hidden group/f flex items-center justify-between gap-2.5">
                            <div className={`absolute top-0 left-0 w-1 h-full ${f.priority === 'High' ? 'bg-red-500' : f.priority === 'Low' ? 'bg-blue-500' : 'bg-amber-500'}`} />

                            <div className="flex-1 min-w-0 pl-1.5">
                              {/* Line 1: Title and Date */}
                              <div className="flex items-center gap-1.5">
                                <p className="font-semibold text-xs leading-tight truncate flex-1 text-foreground">{f.title}</p>
                                <span className="text-[10px] text-muted-foreground font-mono shrink-0 flex items-center gap-1">
                                  <Calendar className="h-2.5 w-2.5 text-muted-foreground/80" /> {f.scheduledDate}
                                  {f.scheduledTime && (
                                    <>
                                      <Clock className="h-2.5 w-2.5 ml-0.5 text-muted-foreground/80" /> {formatTimeAMPM(f.scheduledTime)}
                                    </>
                                  )}
                                </span>
                              </div>

                              {/* Line 2: Description and Staff */}
                              <div className="flex items-center justify-between gap-2 mt-0.5">
                                <p className="text-[11px] text-muted-foreground truncate italic flex-1">
                                  {f.description ? `"${f.description}"` : "No description"}
                                </p>
                                <span className="text-[9px] text-muted-foreground/80 shrink-0 font-medium font-sans">
                                  {f.assignedStaffName || 'Unassigned'}
                                </span>
                              </div>
                            </div>

                            {/* Status Badges */}
                            <div className="shrink-0 flex flex-col items-end gap-0.5">
                              <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(f)] || "bg-cyan-500/15 text-cyan-500 border-cyan-500/20"} text-[8px] h-3.5 px-1 font-mono uppercase`}>
                                {getTemporalStatus(f)}
                              </Badge>
                              <Badge className={`${followupStatusColor(f.status)} text-[8px] h-3.5 px-1 font-mono uppercase`}>
                                {f.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>

                      <Button variant="outline" size="sm" className="w-full text-xs border-dashed hover:border-primary  transition-all"
                        onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}?tab=followups`); }}>
                        <Plus className="h-3.5 w-3.5 mr-2" /> Add New Follow-up
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 px-4 border-2 border-dashed border-border rounded-xl bg-secondary/10">
                      <MessageSquare className="h-8 w-8 text-muted-foreground mb-3 opacity-30" />
                      <p className="text-xs text-muted-foreground text-center mb-4">No active follow-ups scheduled yet.</p>
                      <Button variant="outline" size="sm" className="text-xs gap-2" onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}?tab=followups`); }}>
                        <Plus className="h-3.5 w-3.5" /> Schedule First Follow-up
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )
          }
          {/* Right Column: Follow-up info */}
        </div>
      </div>
    );
  };
  const activeFilterCount = Object.values(filters).filter(v => {
    if (Array.isArray(v)) return v.length > 0;
    return !!v;
  }).length;

  const pageTitle = filters.status === "Expired" ? "Expired Members" : "Members";

  const statCards = [
    { label: "TOTAL", value: stats.total || 0, icon: Users },
    { label: "ACTIVE", value: stats.active || 0, icon: UserCheck, color: "text-emerald-500" },
    { label: "EXPIRED", value: stats.expired || 0, icon: UserX, color: "text-red-500" },
    { label: "PARTIAL", value: stats.partial || 0, icon: Clock, color: "text-amber-500" },
    { label: "NEW MTD", value: stats.newThisMonth || 0, icon: UserPlus, color: "text-blue-500" },
  ];

  const MembershipOverviewDialog = () => {
    const m = membershipDialog.member;
    if (!m) return null;
    const allMs = m.allMemberships || [];
    return (
      <Dialog open={membershipDialog.open} onOpenChange={(o) => setMembershipDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="bg-card border-border max-w-2xl w-full">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-primary" />
              Memberships — {m.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {allMs.length} membership{allMs.length !== 1 ? "s" : ""} found
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-5 max-h-[60vh] overflow-y-auto pr-1 py-1">
            {allMs.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">No memberships found.</p>
            ) : allMs.map((ms) => (
              <div key={ms.id} className="rounded-xl border border-border bg-secondary/30 p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Package className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-semibold text-sm truncate">{ms.packageName || getPackageName(ms.packageId) || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`${statusColor(ms.status)} border text-[10px] font-mono uppercase`}>{ms.status}</Badge>
                    <Button size="sm" variant="outline" className="h-7 text-xs font-heading uppercase tracking-wide gap-1"
                      onClick={() => { setMembershipDialog(d => ({ ...d, open: false })); navigate(`/memberships/${ms.id}`); }}>
                      View <ArrowRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <p className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1 mb-0.5"><CalendarDays className="h-3 w-3" /> Start</p>
                    <p className="font-mono">{ms.startDate?.slice(0, 10) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1 mb-0.5"><CalendarDays className="h-3 w-3" /> End</p>
                    <p className="font-mono">{ms.endDate?.slice(0, 10) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1 mb-0.5"><Wallet className="h-3 w-3" /> Paid</p>
                    <p className="font-mono text-emerald-500">{currencySymbol}{(ms.paidAmount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1 mb-0.5"><Wallet className="h-3 w-3" /> Due</p>
                    <p className={`font-mono ${ms.remainingAmount > 0 ? "text-amber-500" : "text-muted-foreground"}`}>
                      {ms.remainingAmount > 0 ? `${currencySymbol}${ms.remainingAmount.toLocaleString()}` : "—"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="pt-3 border-t border-border">
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground"
              onClick={() => { setMembershipDialog(d => ({ ...d, open: false })); navigate(`/members/${m.id}?tab=memberships`); }}>
              <Eye className="h-3.5 w-3.5 mr-1" /> View Member Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // QuickAttendanceDialog extracted to components/QuickAttendanceDialog.js


  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-2 mt-2 md:space-y-3" data-testid="members-page">

      {/* ── Header ── */}
      <div className="flex sm:flex-row sm:items-center justify-between gap-2 md:gap-4 ">
        <div>
          <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">{pageTitle}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant={activeFilterCount > 0 ? "secondary" : "outline"} size="sm"
            onClick={() => setShowFilters(true)} className="font-heading uppercase tracking-wide text-xs" data-testid="toggle-filters-btn">
            <Filter className="h-4 w-4" /> <span className="hidden sm:block">Filters</span>
            {activeFilterCount > 0 && (
              <Badge className="bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px]">{activeFilterCount}</Badge>
            )}
          </Button>
          {canExport && (
            <Button variant="outline" size="sm" onClick={handleExport} className="font-heading uppercase tracking-wide text-xs" data-testid="export-members-btn">
              <Download className="h-4 w-4 " /><span className="hidden md:block">Export</span>
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => navigate("/members/new")} className="font-heading uppercase tracking-wide text-xs" data-testid="add-member-btn">
              <Plus className="h-4 w-4 " /><span className="">Add Member</span>
            </Button>
          )}
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="flex overflow-x-auto gap-2 pb-1 -mx-4 px-4 md:grid md:grid-cols-5 md:gap-3 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {statCards.map((s, i) => (
          <Card key={i} className="bg-card border-border group hover:border-primary/40 transition-all min-w-[105px] md:min-w-0 flex-1 shrink-0">
            <CardContent className="p-2.5">
              <div className="flex items-center gap-1 sm:gap-2">
                <s.icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${s.color || "text-muted-foreground"} group-hover:text-primary transition-colors shrink-0`} />
                <p className="text-[8px] sm:text-[10px] text-muted-foreground font-mono uppercase tracking-wider sm:tracking-widest leading-tight truncate">{s.label}</p>
              </div>
              <p className={`text-base sm:text-2xl font-black font-heading mt-1 sm:mt-1.5 ${s.color || ""}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Filter Sheet ── */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className={isDesktop
            ? "w-[400px] flex flex-col p-0 bg-card border-border"
            : "rounded-t-2xl flex flex-col p-0 max-h-[92vh] bg-card border-border"
          }
        >
          <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">{activeFilterCount} active</Badge>
                )}
              </SheetTitle>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground text-xs h-7">
                  <X className="h-3.5 w-3.5 mr-1" />Clear all
                </Button>
              )}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Search Name</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Member name..." value={filters.name}
                    onChange={(e) => setFilters(f => ({ ...f, name: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pl-9" data-testid="filter-name" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Phone..." value={filters.phone}
                    onChange={(e) => setFilters(f => ({ ...f, phone: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pl-9" data-testid="filter-phone" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Status</Label>
                <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Partial">Partial</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Gender</Label>
                <Select value={filters.gender || "all"} onValueChange={(v) => setFilters(f => ({ ...f, gender: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="filter-gender"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Packages</Label>
              <MultiSelect
                options={packages.map(p => ({ label: p.name, value: p.id }))}
                selected={filters.packageIds}
                onChange={(vals) => setFilters(f => ({ ...f, packageIds: vals }))}
                placeholder="Select packages..."
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Joining Date Range</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <DatePicker value={filters.joiningFrom} onChange={(v) => setFilters(f => ({ ...f, joiningFrom: v }))} placeholder="From" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <DatePicker value={filters.joiningTo} onChange={(v) => setFilters(f => ({ ...f, joiningTo: v }))} placeholder="To" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Expiry Date Range</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <DatePicker value={filters.expiryFrom} onChange={(v) => setFilters(f => ({ ...f, expiryFrom: v }))} placeholder="From" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <DatePicker value={filters.expiryTo} onChange={(v) => setFilters(f => ({ ...f, expiryTo: v }))} placeholder="To" />
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-border bg-card shrink-0">
            <Button className="w-full font-heading uppercase tracking-wide text-xs" onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Table + Mobile Cards ── */}
      <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0  md:mx-0 rounded-lg">

        {/* Desktop table */}
        <div className="hidden md:block overflow-auto flex-1">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10 py-1.5 px-3"></TableHead>
                <TableHead className="py-1.5 px-3">Member</TableHead>
                <TableHead className="py-1.5 px-3">Phone</TableHead>
                <TableHead className="py-1.5 px-3">Package / Membership</TableHead>
                <TableHead className="py-1.5 px-3">Amount</TableHead>
                <TableHead className="py-1.5 px-3 text-center w-16">Alerts</TableHead>
                <TableHead className="py-1.5 px-3">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="[&_tr:last-child]:border-1">
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12">
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                    <span className="text-muted-foreground text-sm">Loading...</span>
                  </div>
                </TableCell></TableRow>
              ) : members.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No members found</TableCell></TableRow>
              ) : members.map((m) => (
                <Fragment key={m.id}>
                  <TableRow
                    className="group cursor-pointer hover:bg-secondary/50"
                    data-testid={`member-row-${m.id}`}
                    onClick={() => toggleRow(m.id)}
                  >
                    <TableCell className="w-10 py-1.5 px-3">
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expandedRowId === m.id ? "rotate-180" : ""}`} />
                      </Button>
                    </TableCell>
                    <TableCell className="py-1.5 px-3">
                      <div className="flex items-center gap-2.5">
                        <div onClick={(e) => openPhoto(e, m)} className={m.photo ? "cursor-zoom-in" : "cursor-default"} title={m.photo ? "View photo" : undefined}>
                          <Avatar className={`h-8 w-8 border border-border transition-opacity ${m.photo ? "hover:opacity-80" : ""}`}>
                            {m.photo ? <AvatarImage src={`${BACKEND_URL}${m.photo}`} /> : null}
                            <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading">{m.name?.[0]?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                        </div>
                        <div className="cursor-pointer hover:text-primary transition-colors">
                          <p className="font-medium text-xs leading-tight"><span className="text-muted-foreground text-[10px]">#{m.memberNumber}</span> {m.name}</p>
                          <p className="text-[9px] text-muted-foreground font-mono uppercase">{m.gender}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs py-1.5 px-3">{m.phone}</TableCell>
                    <TableCell className="py-1.5 px-3">
                      {m.membership ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground">
                            <Package className="h-3 w-3 shrink-0 text-primary/75" />
                            <span className="truncate max-w-[130px]">{m.membership.packageName || getPackageName(m.membership.packageId) || "—"}</span>
                          </span>
                          {m.membershipCount > 1 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className="text-[9px] font-mono px-1 py-0 h-3.5 w-fit cursor-help"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    +{m.membershipCount - 1} more
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="bg-popover text-popover-foreground border border-border p-2.5 max-w-[280px]" onClick={(e) => e.stopPropagation()}>
                                  <div className="space-y-1.5">
                                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground border-b border-border pb-1 mb-1">Remaining Plans</p>
                                    {(m.allMemberships || [])
                                      .filter((ms) => ms.id !== m.membership?.id)
                                      .map((ms) => (
                                        <div key={ms.id} className="flex flex-col gap-0.5 text-xs">
                                          <div className="flex items-center gap-1.5">
                                            <span className="font-medium">{ms.packageName || getPackageName(ms.packageId) || "—"}</span>
                                            <Badge className={`${statusColor(ms.status)} border text-[8px] font-mono uppercase px-1 py-0 h-3.5`}>{ms.status}</Badge>
                                          </div>
                                          {ms.startDate && ms.endDate && (
                                            <p className="text-[10px] text-muted-foreground font-mono">{ms.startDate.slice(0, 10)} → {ms.endDate.slice(0, 10)}</p>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      ) : canAssign ? (
                        <Button variant="outline" size="sm" className="text-[10px] h-6 py-0 px-2" onClick={(e) => handleAssign(e, m)} data-testid={`assign-membership-${m.id}`}>Assign</Button>
                      ) : <span className="text-muted-foreground text-[11px]">No membership</span>}
                    </TableCell>
                    <TableCell className="py-1.5 px-3">
                      {m.membershipCount > 0 ? (
                        <div className="text-xs">
                          <p className="font-mono font-medium">{currencySymbol}{(m.totalPaid || 0).toLocaleString()}</p>
                          {(m.totalRemaining || 0) > 0 && (
                            <p className="text-[9px] text-amber-500 font-mono">Due: {currencySymbol}{m.totalRemaining.toLocaleString()}</p>
                          )}
                        </div>
                      ) : <span className="text-muted-foreground text-xs">-</span>}
                    </TableCell>
                    <TableCell className="py-1.5 px-3 text-center">
                      {(() => {
                        const overdueFollowups = m.activeFollowups?.filter(f => getTemporalStatus(f) === "Overdue") || [];
                        const hasOverdueFollowup = overdueFollowups.length > 0;
                        if (!hasOverdueFollowup) return null;
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="inline-flex items-center justify-center text-red-500 hover:text-red-600 bg-red-500/10 hover:bg-red-500/20 p-1.5 rounded-full transition-colors cursor-help">
                                  <Bell className="h-3.5 w-3.5 animate-pulse" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-popover text-popover-foreground border border-border p-2.5 max-w-[280px]" onClick={(e) => e.stopPropagation()}>
                                <div className="space-y-1">
                                  <p className="font-bold text-xs text-red-500 flex items-center gap-1">
                                    <Bell className="h-3 w-3" /> Overdue Follow-up ({overdueFollowups.length})
                                  </p>
                                  <div className="space-y-1.5 mt-1 border-t border-border pt-1">
                                    {overdueFollowups.map((f) => (
                                      <div key={f.id} className="text-[11px] leading-snug text-left">
                                        <p className="font-medium text-foreground">{f.title}</p>
                                        <p className="text-muted-foreground font-mono text-[10px]">
                                          Due: {f.scheduledDate} {f.scheduledTime && formatTimeAMPM(f.scheduledTime)}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="py-1.5 px-3">
                      <div className="flex items-start flex-col gap-0.5">
                        <Badge className={`${statusColor(m.membership?.status || m.status || "New")} border text-[9px] font-mono uppercase py-0 px-1`}>
                          {displayStatus(m.membership?.status || m.status)}
                        </Badge>
                        {m.membership?.status == "Partial" && (
                          <span className="text-[9px] font-mono text-muted-foreground">{
                            m.membership?.partialPaymentDate
                          } </span>
                        )}
                        {m.membership?.status !== "Partial" && (
                          <span className="text-[9px] font-mono text-muted-foreground">{
                            m.membership?.endDate
                          } </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>

                  {expandedRowId === m.id && (
                    <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border animate-in fade-in duration-200">
                      <TableCell colSpan={7} className="p-0">
                        {renderMemberDetails(m)}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* ── Mobile Card List ── */}
        <div className="md:hidden overflow-x-hidden overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
              <span className="text-muted-foreground text-sm ml-2">Loading...</span>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No members found</div>
          ) : members.map((m, idx) => (
            <Collapsible
              key={m.id}
              open={expandedRowId === m.id}
              onOpenChange={() => toggleRow(m.id)}
              asChild
            >
              <div data-testid={`member-card-${m.id}`}
                className={` border-b border-border hover:bg-secondary/10 transition-colors`}>

                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-2 py-1.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground p-0 hover:text-foreground shrink-0">
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${expandedRowId === m.id ? "rotate-180" : ""}`} />
                        </Button>
                        <div onClick={(e) => openPhoto(e, m)} className={m.photo ? "cursor-zoom-in shrink-0" : "cursor-default shrink-0"} >
                          <Avatar className={`h-8 w-8 border border-border ${m.photo ? "hover:opacity-80 transition-opacity" : ""}`}>
                            {m.photo ? <AvatarImage src={`${BACKEND_URL}${m.photo}`} /> : null}
                            <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading font-bold">{m.name?.[0]?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          {m.memberNumber != null && (
                            <span className="text-[10px] font-mono text-muted-foreground shrink-0">#{m.memberNumber}</span>
                          )}
                          <p className="font-semibold text-xs leading-tight truncate">{m.name}</p>
                          {(() => {
                            const overdueFollowups = m.activeFollowups?.filter(f => getTemporalStatus(f) === "Overdue") || [];
                            if (overdueFollowups.length > 0) {
                              return (
                                <span className="flex h-2 w-2 relative shrink-0">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" title={`Overdue Followup: ${overdueFollowups[0].title}`}></span>
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <span>{m.gender}</span>
                          <span className="text-border/60">|</span>
                          <p className="font-mono text-[10px]">{m.phone}</p>
                        </div>
                        {m.membership ? (
                          <div className="flex items-center gap-1 mt-0.5 text-[10px] font-mono text-muted-foreground">
                            <Package className="h-2.5 w-2.5 shrink-0 text-primary/70" />
                            <span className="truncate max-w-[100px]">{m.membership.packageName || getPackageName(m.membership.packageId) || "—"}</span>
                            {m.membershipCount > 1 && (
                              <span className="text-[9px] text-muted-foreground">+{m.membershipCount - 1} more</span>
                            )}
                          </div>
                        ) : canAssign ? (
                          <div className="mt-0.5" onClick={(e) => handleAssign(e, m)}>
                            <Badge variant="outline" className="text-[8px] font-mono px-1 py-0 h-3.5 cursor-pointer hover:bg-primary/10 hover:text-primary transition-colors leading-none">Assign Plan</Badge>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-[9px] italic block mt-0.5">No membership</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-end flex-col gap-0.5 text-right">
                        <Badge className={`${statusColor(m.membership?.status || m.status || "New")} border text-[8px] font-mono uppercase py-0 px-1`}>
                          {displayStatus(m.membership?.status || m.status)}
                        </Badge>
                        {m.membership?.status !== "Partial" && m.membership?.endDate && (
                          <span className="text-[9px] font-mono text-muted-foreground">
                            {m.membership.endDate}
                          </span>
                        )}
                        {m.membership?.status === "Partial" && m.membership?.partialPaymentDate && (
                          <span className="text-[9px] font-mono text-muted-foreground">
                            {m.membership.partialPaymentDate}
                          </span>
                        )}
                        {m.membershipCount > 0 ? (
                          <div className="mt-0.5 leading-none">
                            <p className="font-mono font-semibold text-[10px] text-foreground">{currencySymbol}{(m.totalPaid || 0).toLocaleString()}</p>
                            {(m.totalRemaining || 0) > 0 && (
                              <p className="text-[9px] text-amber-500 font-mono mt-0.5">Due: {currencySymbol}{m.totalRemaining.toLocaleString()}</p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className=" border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
                    {renderMemberDetails(m)}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>

        {/* ── Pagination ── */}
        <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="font-mono hidden sm:block">{total} total</span>
            <div className="flex items-center gap-2">
              <span className="text-xs">Show:</span>
              <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}><ChevronLeft /></Button>
            <span className="sm:text-sm text-xs text-muted-foreground px-2">Page {page} of {Math.ceil(total / limit) || 1}</span>
            <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(page + 1)}><ChevronRight /></Button>
          </div>
        </div>
      </Card>

      <MembershipOverviewDialog />

      <MembershipModal
        open={!!showMembershipModal}
        onOpenChange={() => setShowMembershipModal(null)}
        member={showMembershipModal}
        membership={showMembershipModal?.membership}
        packages={packages.filter(p => p.status === "Active")}
        branchId={currentBranch?.id}
        onSuccess={fetchData}
      />

      <Dialog open={pkgGuard.open} onOpenChange={(o) => setPkgGuard(g => ({ ...g, open: o }))}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500/10 mx-auto mb-3">
              <PackageX className="h-7 w-7 text-amber-500" />
            </div>
            <DialogTitle className="font-heading uppercase tracking-wide text-center">
              {pkgGuard.reason === "none" ? "No Packages Found" : "No Active Packages"}
            </DialogTitle>
            <DialogDescription className="text-center text-sm text-muted-foreground">
              {pkgGuard.reason === "none"
                ? "You need to create at least one package before assigning a membership."
                : "All existing packages are set to Inactive. Enable at least one package to assign a membership."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <Button className="w-full font-heading uppercase tracking-wide" onClick={() => { setPkgGuard(g => ({ ...g, open: false })); navigate("/packages"); }}>
              <Package className="h-4 w-4 mr-2" />{pkgGuard.reason === "none" ? "Create a Package" : "Manage Packages"}
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setPkgGuard(g => ({ ...g, open: false }))}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={photoDialog.open} onOpenChange={(o) => setPhotoDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="bg-card border-border max-w-sm p-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">{photoDialog.name}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center mt-2">
            <img src={photoDialog.src} alt={photoDialog.name} className="rounded-sm max-h-80 w-full object-contain border border-border" />
          </div>
        </DialogContent>
      </Dialog>

      <QuickAttendanceDialog
        open={quickAttendanceDialog.open}
        onOpenChange={(o) => setQuickAttendanceDialog(d => ({ ...d, open: o }))}
        member={quickAttendanceDialog.member}
        branchId={currentBranch?.id}
      />

      <DeleteConfirmModal
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal({ open, member: open ? deleteModal.member : null })}
        title="Delete Member"
        description="Are you sure you want to delete this member? This action cannot be undone."
        itemName={deleteModal.member?.name}
        onConfirm={() => handleDelete(deleteModal.member?.id)}
        loading={deleteLoading}
      />
    </div>
  );
}
